import React, { useState } from "react";
import { Bus, MapPin, Calendar, ArrowLeftRight, Search, Clock, Star, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { BUS_ROUTES } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const BusSearch = () => {
  const { toast } = useToast();
  const today = new Date().toISOString().slice(0, 10);

  const [q, setQ] = useState({ from: "", to: "", date: today });
  const [results, setResults] = useState(BUS_ROUTES);
  const [selected, setSelected] = useState(null);
  const [pax, setPax] = useState({ name: "", phone: "" });
  const [confirmed, setConfirmed] = useState(null);

  const search = (e) => {
    e.preventDefault();
    if (!q.from || !q.to) {
      toast({ title: "Please add cities", description: "Enter both origin and destination." });
      return;
    }
    const filtered = BUS_ROUTES.filter(
      (b) =>
        b.from.toLowerCase().includes(q.from.toLowerCase()) ||
        b.to.toLowerCase().includes(q.to.toLowerCase())
    );
    setResults(filtered.length ? filtered : BUS_ROUTES);
    toast({
      title: `${filtered.length || BUS_ROUTES.length} buses found`,
      description: `${q.from || "Anywhere"} → ${q.to || "Anywhere"} on ${q.date}`,
    });
  };

  const swap = () => setQ({ ...q, from: q.to, to: q.from });

  const closeDialog = () => {
    setSelected(null);
    setConfirmed(null);
    setPax({ name: "", phone: "" });
  };

  const confirmBooking = (e) => {
    e.preventDefault();
    if (!pax.name || !pax.phone) {
      toast({ title: "Details required", description: "Please enter name & phone." });
      return;
    }
    setConfirmed({ code: "PMRB" + Date.now().toString().slice(-8), data: selected });
  };

  return (
    <section id="buses" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold tracking-wider uppercase">
            <Bus className="w-3.5 h-3.5" /> Bus Booking
          </div>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Reserve your seat in minutes
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Real-time seat availability on our own Volvo fleet plus 200+ partner operators — with the transparent PMR fare guarantee.
          </p>
        </div>

        <form
          onSubmit={search}
          className="mt-10 bg-white rounded-2xl shadow-xl shadow-slate-900/5 border border-slate-100 p-3 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_1fr_auto] gap-2 items-center"
        >
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50">
            <MapPin className="w-5 h-5 text-teal-600" />
            <div className="flex-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">From</label>
              <Input
                value={q.from}
                onChange={(e) => setQ({ ...q, from: e.target.value })}
                placeholder="Churu"
                className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900 placeholder:text-slate-400"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={swap}
            className="hidden md:flex w-10 h-10 rounded-full bg-slate-100 hover:bg-teal-600 hover:text-white items-center justify-center text-slate-600 transition"
            aria-label="swap"
          >
            <ArrowLeftRight className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100">
            <MapPin className="w-5 h-5 text-orange-500" />
            <div className="flex-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">To</label>
              <Input
                value={q.to}
                onChange={(e) => setQ({ ...q, to: e.target.value })}
                placeholder="Delhi"
                className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900 placeholder:text-slate-400"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100">
            <Calendar className="w-5 h-5 text-teal-600" />
            <div className="flex-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Journey Date</label>
              <Input
                type="date"
                value={q.date}
                onChange={(e) => setQ({ ...q, date: e.target.value })}
                className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900"
              />
            </div>
          </div>
          <Button type="submit" className="h-14 md:h-auto md:aspect-square md:w-14 bg-teal-600 hover:bg-teal-700 rounded-xl">
            <Search className="w-5 h-5" />
            <span className="md:hidden ml-2">Search Buses</span>
          </Button>
        </form>

        <div className="mt-10">
          <div className="flex items-end justify-between mb-4">
            <h3 className="text-xl font-bold text-slate-900">Available Buses</h3>
            <span className="text-sm text-slate-500">{results.length} results</span>
          </div>
          <div className="space-y-3">
            {results.map((b) => (
              <div
                key={b.id}
                className="group bg-white rounded-xl border border-slate-200 hover:border-teal-500 hover:shadow-lg transition p-5 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-4 md:gap-6 items-center"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center">
                    <Bus className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">{b.operator}</div>
                    <div className="text-xs text-slate-500">{b.type}</div>
                    <div className="mt-1 flex items-center gap-2 text-[11px]">
                      <span className="flex items-center gap-0.5 text-orange-600 font-semibold">
                        <Star className="w-3 h-3 fill-orange-500 text-orange-500" /> {b.rating}
                      </span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-500">{b.seats} seats left</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 md:gap-6">
                  <div>
                    <div className="text-xl font-bold text-slate-900 leading-none">{b.dep}</div>
                    <div className="text-xs text-slate-500 mt-1">{b.from}</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center">
                    <div className="text-[11px] text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {b.duration}
                    </div>
                    <div className="w-full h-px bg-slate-200 relative my-1">
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-teal-600" />
                    </div>
                    <div className="text-[11px] text-slate-400">Non-stop</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-slate-900 leading-none">{b.arr}</div>
                    <div className="text-xs text-slate-500 mt-1">{b.to}</div>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-[11px] text-slate-500">Starting</div>
                  <div className="text-xl font-bold text-teal-700">₹{b.price.toLocaleString("en-IN")}</div>
                  <Button
                    onClick={() => setSelected(b)}
                    className="mt-2 bg-orange-500 hover:bg-orange-600 text-white h-9"
                  >
                    Book Seat <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && closeDialog()}>
        <DialogContent className="max-w-lg">
          {selected && !confirmed && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Book Bus Seat
                </DialogTitle>
                <DialogDescription>
                  {selected.operator} · {selected.from} → {selected.to} · {selected.dep}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={confirmBooking} className="mt-4 space-y-4">
                <div>
                  <Label htmlFor="bname" className="text-xs">Full Name *</Label>
                  <Input id="bname" value={pax.name} onChange={(e) => setPax({ ...pax, name: e.target.value })} placeholder="Ravi Kumar" />
                </div>
                <div>
                  <Label htmlFor="bphone" className="text-xs">Phone *</Label>
                  <Input id="bphone" value={pax.phone} onChange={(e) => setPax({ ...pax, phone: e.target.value })} placeholder="+91 98765 43210" />
                </div>
                <div className="pt-3 border-t border-slate-100 flex items-end justify-between">
                  <div>
                    <div className="text-xs text-slate-500">Total (approx.)</div>
                    <div className="text-2xl font-bold text-slate-900">₹{selected.price.toLocaleString("en-IN")}</div>
                  </div>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700">Confirm Booking</Button>
                </div>
              </form>
            </>
          )}

          {confirmed && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-green-600" />
              </div>
              <h3 className="mt-5 text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Bus Seat Booked!
              </h3>
              <p className="mt-2 text-slate-600 text-sm">
                A confirmation SMS has been sent to your phone. Our team will call you within 30 minutes.
              </p>
              <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-left space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Booking ID</span><span className="font-mono font-semibold">{confirmed.code}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Bus</span><span className="font-semibold">{confirmed.data.operator}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Passenger</span><span className="font-semibold">{pax.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Phone</span><span className="font-semibold">{pax.phone}</span></div>
              </div>
              <Button onClick={closeDialog} className="mt-6 bg-teal-600 hover:bg-teal-700">Done</Button>
              <p className="mt-4 text-[11px] text-orange-600 font-semibold">DEMO MODE — Booking is MOCKED (no real transaction).</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default BusSearch;
